import { useModal } from "@/features/modal/ModalStackManager";
import { Calendar, CalendarDate } from "@/features/ui/calendar";
import { AnalysisPressChart } from "@/features/ui/PMS/chart/AnalysisPressChart";
import { Machine, MachineSearchBox } from "@/features/ui/PMS/machineSearchBox";
import { Button, Flex, Text } from "@mantine/core";
import { IconCalendar, IconReportSearch } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useState } from "react";
import DataGrid, { Column } from "react-data-grid";

export const AnalysisOutputPage = () => {
  const { openModal, closeModal } = useModal();
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [dates, setDates] = useState<CalendarDate>([null, null]);
  const [itemCode, setItemCode] = useState<string>("");

  const handleSelectMachine = (machine: Machine | null) => {
    if (machine) {
      setSelectedMachine(machine);
      closeModal("");
    }
  };

  const moldColumns: readonly Column<any>[] = [
    {
      key: "moldName",
      name: "금형명",
      sortable: true,
      resizable: true,
    },
    {
      key: "changeTime",
      name: "금형 교체 시작 시간 ~ 완료 시간",
      sortable: true,
      resizable: true,
      width: 250,
    },
  ];

  const errorColumns: readonly Column<any>[] = [
    {
      key: "errorNumber",
      name: "에러 번호",
      sortable: true,
      resizable: true,
    },
    {
      key: "errorName",
      name: "에러 명칭",
      sortable: true,
      resizable: true,
    },
    {
      key: "errorTime",
      name: "에러 발생 시간",
      sortable: true,
      resizable: true,
    },
  ];

  const workColumns: readonly Column<any>[] = [
    {
      key: "workTime",
      name: "제품별 가동 시간",
      sortable: true,
      resizable: true,
    },
    {
      key: "idleTime",
      name: "제품별 비가동 시간",
      sortable: true,
      resizable: true,
    },
  ];
  const productionColumns: readonly Column<any>[] = [
    {
      key: "day",
      name: "제품별 일 생산 수량",
      sortable: true,
      resizable: true,
    },
    {
      key: "hour",
      name: "제품별 시간당 평균 생산 수량",
      sortable: true,
      resizable: true,
    },
  ];

  const machineColumns: readonly Column<any>[] = [
    {
      key: "make",
      name: "기계 제조사",
      sortable: true,
      resizable: true,
    },
    {
      key: "name",
      name: "기계 이름",
      sortable: true,
      resizable: true,
    },
    {
      key: "createdAt",
      name: "제조 연월일",
      sortable: true,
      resizable: true,
    },
    {
      key: "code",
      name: "제조 번호",
      sortable: true,
      resizable: true,
    },
  ];

  const itemColumns: readonly Column<any>[] = [
    {
      key: "model",
      name: "모델",
      sortable: true,
      resizable: true,
    },
    {
      key: "code",
      name: "코드",
      sortable: true,
      resizable: true,
    },
    {
      key: "name",
      name: "품명",
      sortable: true,
      resizable: true,
    },

    {
      key: "spec",
      name: "규격",
      sortable: true,
      resizable: true,
    },
  ];

  return (
    <Flex gap="md" direction="column" p="md">
      <Flex justify="space-between">
        <Text size="xl">생산량</Text>
        <Flex gap="md">
          <Button
            leftIcon={<IconCalendar />}
            color="lime"
            onClick={() => {
              openModal(<Calendar dates={[null, null]} type="default" />, null, "날짜 선택").then(
                (selectedDates) => {
                  if (selectedDates) {
                    setDates(selectedDates);
                  }
                },
              );
            }}
          >
            {`${dates[0] !== null ? dayjs(dates[0]).format("YYYY-MM-DD") : ""} 날짜선택`}
          </Button>
          <Button
            onClick={() => {
              openModal(<MachineSearchBox onSelect={handleSelectMachine} />, null, "기계 검색");
            }}
            leftIcon={<IconReportSearch />}
          >
            설비선택
          </Button>
        </Flex>
      </Flex>
      <DataGrid
        columns={machineColumns ?? []}
        rows={selectedMachine ? [selectedMachine] : []}
        rowHeight={40}
        rowKeyGetter={(row) => row.id}
        style={{ height: "120px", width: "100%" }}
      />
      <Flex gap="md">
        <DataGrid
          columns={itemColumns ?? []}
          rows={
            Array.isArray(machineItemMockupData[selectedMachine?.id ?? 0])
              ? machineItemMockupData[selectedMachine?.id ?? 0]
              : []
          }
          rowHeight={40}
          rowKeyGetter={(row) => row.code}
          onRowClick={(row) => {
            setItemCode(row.code);
          }}
          style={{ height: "100%", width: "30%" }}
        />
        <AnalysisPressChart itemCode={itemCode} mode="chart" />
      </Flex>
      <Flex gap="md" w="100%">
        <Flex direction={"column"} gap="md" w={"25%"}>
          <DataGrid
            columns={productionColumns ?? []}
            rows={
              itemCode && itemDetailMockupData[itemCode]?.production
                ? [itemDetailMockupData[itemCode].production]
                : []
            }
            rowHeight={40}
            style={{ height: "80px", width: "100%" }}
          />
          <DataGrid
            columns={workColumns ?? []}
            rows={
              itemCode && itemDetailMockupData[itemCode]?.work
                ? [itemDetailMockupData[itemCode].work]
                : []
            }
            rowHeight={40}
            style={{ height: "80px", width: "100%" }}
          />
        </Flex>
        <DataGrid
          columns={errorColumns ?? []}
          rows={
            itemCode && Array.isArray(itemDetailMockupData[itemCode]?.error)
              ? itemDetailMockupData[itemCode].error
              : []
          }
          rowHeight={40}
          style={{ height: "100%", width: "45%" }}
        />

        <DataGrid
          columns={moldColumns ?? []}
          rows={
            itemCode && Array.isArray(itemDetailMockupData[itemCode]?.mold)
              ? itemDetailMockupData[itemCode].mold
              : []
          }
          rowHeight={40}
          style={{ height: "100%", width: "25%" }}
        />
      </Flex>
    </Flex>
  );
};

export const machineItemMockupData: Record<
  string,
  Array<{
    model: string;
    code: string;
    name: string;
    spec: string;
  }>
> = {
  "1": [
    { model: "X100", code: "ITM001", name: "이젝터1", spec: "10x10mm" },
    { model: "X101", code: "ITM002", name: "이젝터2", spec: "12x12mm" },
    { model: "X102", code: "ITM003", name: "예비캠1", spec: "20x20mm" },
  ],
  "2": [
    { model: "X103", code: "ITM004", name: "예비캠2", spec: "14x18mm" },
    { model: "X104", code: "ITM005", name: "예비캠3", spec: "25x30mm" },
  ],
  "3": [
    { model: "X105", code: "ITM006", name: "프레스 커버", spec: "30x30mm" },
    { model: "X106", code: "ITM007", name: "고정 플레이트", spec: "40x40mm" },
  ],
  "4": [
    { model: "X107", code: "ITM008", name: "센서 플레이트", spec: "15x15mm" },
    { model: "X108", code: "ITM009", name: "로드 블록", spec: "60mm" },
    { model: "X109", code: "ITM010", name: "엔드 스토퍼", spec: "22x22mm" },
  ],
  "5": [
    { model: "X110", code: "ITM011", name: "모터 커버", spec: "50x50mm" },
    { model: "X111", code: "ITM012", name: "핀 고정부", spec: "28x28mm" },
  ],
  "6": [
    { model: "X112", code: "ITM013", name: "슬라이더", spec: "80x30mm" },
    { model: "X113", code: "ITM014", name: "센터 블럭", spec: "35x35mm" },
    { model: "X114", code: "ITM015", name: "고정 롤러", spec: "Ø40mm" },
  ],
  "7": [
    { model: "X115", code: "ITM016", name: "유압 실린더", spec: "Ø100mm" },
    { model: "X116", code: "ITM017", name: "스트로크 가이드", spec: "60x10mm" },
  ],
  "8": [
    { model: "X117", code: "ITM018", name: "프레임 조인트", spec: "70x30mm" },
    { model: "X118", code: "ITM019", name: "기어 플레이트", spec: "80x80mm" },
    { model: "X119", code: "ITM020", name: "베어링 커버", spec: "Ø65mm" },
  ],
  "9": [
    { model: "X120", code: "ITM021", name: "조정 너트", spec: "M12x1.5" },
    { model: "X121", code: "ITM022", name: "가이드 핀", spec: "Ø10x50mm" },
  ],
  "10": [
    { model: "X122", code: "ITM023", name: "베이스 플레이트", spec: "100x100mm" },
    { model: "X123", code: "ITM024", name: "체결 브라켓", spec: "45x20mm" },
    { model: "X124", code: "ITM025", name: "전선 고정대", spec: "30x15mm" },
  ],
  "11": [
    { model: "X125", code: "ITM026", name: "클러치", spec: "Ø80mm" },
    { model: "X126", code: "ITM027", name: "브레이크패드", spec: "220x50x10mm" },
  ],
  "12": [
    { model: "X127", code: "ITM028", name: "전원 모듈", spec: "300W" },
    { model: "X128", code: "ITM029", name: "온도 센서", spec: "PT100" },
  ],
  "13": [
    { model: "X129", code: "ITM030", name: "진동 댐퍼", spec: "120x60mm" },
    { model: "X130", code: "ITM031", name: "냉각핀", spec: "70x40mm" },
    { model: "X131", code: "ITM032", name: "커넥터 베이스", spec: "6핀" },
  ],
  "14": [
    { model: "X132", code: "ITM033", name: "릴레이 모듈", spec: "12V" },
    { model: "X133", code: "ITM034", name: "I/O 보드", spec: "16채널" },
  ],
  "15": [
    { model: "X134", code: "ITM035", name: "공압 밸브", spec: "5/2 방식" },
    { model: "X135", code: "ITM036", name: "에어 실린더", spec: "Ø25x75mm" },
  ],
  "16": [
    { model: "X136", code: "ITM037", name: "가스켓", spec: "Ø50mm" },
    { model: "X137", code: "ITM038", name: "커넥팅 로드", spec: "200mm" },
  ],
  "17": [
    { model: "X138", code: "ITM039", name: "윤활 펌프", spec: "자동식" },
    { model: "X139", code: "ITM040", name: "오일 필터", spec: "100μm" },
    { model: "X140", code: "ITM041", name: "탱크 브라켓", spec: "80x60mm" },
  ],
  "18": [
    { model: "X141", code: "ITM042", name: "제어기 커버", spec: "ABS" },
    { model: "X142", code: "ITM043", name: "디스플레이 모듈", spec: "7인치" },
  ],
  "19": [
    { model: "X143", code: "ITM044", name: "서보 모터", spec: "400W" },
    { model: "X144", code: "ITM045", name: "엔코더", spec: "360ppr" },
  ],
  "20": [
    { model: "X145", code: "ITM046", name: "파워서플라이", spec: "24V 5A" },
    { model: "X146", code: "ITM047", name: "단자대", spec: "16핀" },
    { model: "X147", code: "ITM048", name: "안전 커버", spec: "투명 PC" },
  ],
};

const itemDetailMockupData: Record<
  string,
  {
    production: { day: number; hour: number };
    work: { workTime: string; idleTime: string };
    error: Array<{ errorNumber: string; errorName: string; errorTime: string }>;
    mold: Array<{ moldName: string; changeTime: string }>;
  }
> = {
  ITM001: {
    production: { day: 1200, hour: 100 },
    work: { workTime: "08:00", idleTime: "00:30" },
    error: [{ errorNumber: "E101", errorName: "모터 정지", errorTime: "10:15" }],
    mold: [
      { moldName: "금형A", changeTime: "07:00 ~ 07:30" },
      { moldName: "금형B", changeTime: "12:00 ~ 12:20" },
    ],
  },
  ITM002: {
    production: { day: 950, hour: 85 },
    work: { workTime: "07:30", idleTime: "01:00" },
    error: [
      { errorNumber: "E102", errorName: "온도 초과", errorTime: "11:45" },
      { errorNumber: "E103", errorName: "센서 불량", errorTime: "14:10" },
    ],
    mold: [{ moldName: "금형C", changeTime: "06:50 ~ 07:10" }],
  },
  ITM003: {
    production: { day: 1320, hour: 110 },
    work: { workTime: "08:10", idleTime: "00:15" },
    error: [{ errorNumber: "E104", errorName: "압력 이상", errorTime: "09:50" }],
    mold: [
      { moldName: "금형A", changeTime: "08:00 ~ 08:25" },
      { moldName: "금형B", changeTime: "13:30 ~ 13:50" },
    ],
  },
  ITM004: {
    production: { day: 880, hour: 74 },
    work: { workTime: "06:45", idleTime: "01:15" },
    error: [],
    mold: [{ moldName: "금형D", changeTime: "07:20 ~ 07:40" }],
  },
  ITM005: {
    production: { day: 1050, hour: 90 },
    work: { workTime: "07:50", idleTime: "00:45" },
    error: [{ errorNumber: "E105", errorName: "냉각 누수", errorTime: "15:00" }],
    mold: [{ moldName: "금형E", changeTime: "06:40 ~ 07:00" }],
  },
  ITM006: {
    production: { day: 816, hour: 107 },
    work: { workTime: "13:00", idleTime: "00:30" },
    error: [{ errorNumber: "E258", errorName: "압력 이상", errorTime: "07:15" }],
    mold: [
      { moldName: "금형A", changeTime: "15:45 ~ 16:55" },
      { moldName: "금형D", changeTime: "17:15 ~ 18:25" },
    ],
  },
  ITM007: {
    production: { day: 1379, hour: 84 },
    work: { workTime: "09:45", idleTime: "01:30" },
    error: [
      { errorNumber: "E497", errorName: "모터 정지", errorTime: "15:00" },
      { errorNumber: "E811", errorName: "온도 초과", errorTime: "14:30" },
    ],
    mold: [
      { moldName: "금형D", changeTime: "12:15 ~ 13:35" },
      { moldName: "금형D", changeTime: "12:15 ~ 12:25" },
    ],
  },
  ITM008: {
    production: { day: 847, hour: 88 },
    work: { workTime: "08:30", idleTime: "00:15" },
    error: [
      { errorNumber: "E401", errorName: "센서 불량", errorTime: "10:00" },
      { errorNumber: "E970", errorName: "온도 초과", errorTime: "14:15" },
    ],
    mold: [
      { moldName: "금형F", changeTime: "14:15 ~ 14:45" },
      { moldName: "금형E", changeTime: "15:45 ~ 16:15" },
    ],
  },
  ITM009: {
    production: { day: 1162, hour: 74 },
    work: { workTime: "18:45", idleTime: "01:15" },
    error: [
      { errorNumber: "E639", errorName: "모터 정지", errorTime: "14:45" },
      { errorNumber: "E912", errorName: "온도 초과", errorTime: "11:30" },
    ],
    mold: [
      { moldName: "금형E", changeTime: "16:15 ~ 17:35" },
      { moldName: "금형D", changeTime: "17:00 ~ 17:10" },
    ],
  },
  ITM010: {
    production: { day: 1228, hour: 110 },
    work: { workTime: "11:30", idleTime: "01:15" },
    error: [{ errorNumber: "E714", errorName: "압력 이상", errorTime: "13:45" }],
    mold: [
      { moldName: "금형C", changeTime: "15:45 ~ 16:15" },
      { moldName: "금형C", changeTime: "06:45 ~ 08:15" },
    ],
  },
  ITM011: {
    production: { day: 1119, hour: 106 },
    work: { workTime: "18:15", idleTime: "01:15" },
    error: [
      { errorNumber: "E710", errorName: "센서 불량", errorTime: "15:30" },
      { errorNumber: "E686", errorName: "과부하", errorTime: "13:00" },
    ],
    mold: [
      { moldName: "금형C", changeTime: "06:45 ~ 07:55" },
      { moldName: "금형C", changeTime: "14:30 ~ 16:00" },
    ],
  },
  ITM012: {
    production: { day: 984, hour: 89 },
    work: { workTime: "16:15", idleTime: "01:30" },
    error: [{ errorNumber: "E627", errorName: "온도 초과", errorTime: "15:30" }],
    mold: [
      { moldName: "금형A", changeTime: "07:30 ~ 07:40" },
      { moldName: "금형E", changeTime: "07:15 ~ 08:45" },
    ],
  },
  ITM013: {
    production: { day: 920, hour: 71 },
    work: { workTime: "13:15", idleTime: "00:15" },
    error: [
      { errorNumber: "E187", errorName: "압력 이상", errorTime: "16:00" },
      { errorNumber: "E280", errorName: "모터 정지", errorTime: "17:15" },
    ],
    mold: [
      { moldName: "금형F", changeTime: "15:45 ~ 17:05" },
      { moldName: "금형E", changeTime: "10:45 ~ 11:05" },
    ],
  },
  ITM014: {
    production: { day: 1359, hour: 96 },
    work: { workTime: "18:45", idleTime: "00:45" },
    error: [{ errorNumber: "E536", errorName: "센서 불량", errorTime: "16:45" }],
    mold: [{ moldName: "금형B", changeTime: "10:30 ~ 11:00" }],
  },
  ITM015: {
    production: { day: 964, hour: 111 },
    work: { workTime: "12:00", idleTime: "00:30" },
    error: [{ errorNumber: "E778", errorName: "모터 정지", errorTime: "15:45" }],
    mold: [{ moldName: "금형F", changeTime: "11:00 ~ 11:20" }],
  },
  ITM016: {
    production: { day: 926, hour: 92 },
    work: { workTime: "10:45", idleTime: "01:15" },
    error: [
      { errorNumber: "E768", errorName: "온도 초과", errorTime: "11:45" },
      { errorNumber: "E912", errorName: "온도 초과", errorTime: "16:00" },
    ],
    mold: [
      { moldName: "금형B", changeTime: "09:30 ~ 10:40" },
      { moldName: "금형C", changeTime: "18:45 ~ 20:15" },
    ],
  },
  ITM017: {
    production: { day: 1432, hour: 80 },
    work: { workTime: "15:30", idleTime: "00:15" },
    error: [
      { errorNumber: "E325", errorName: "과부하", errorTime: "18:30" },
      { errorNumber: "E120", errorName: "온도 초과", errorTime: "09:15" },
    ],
    mold: [
      { moldName: "금형A", changeTime: "12:45 ~ 14:05" },
      { moldName: "금형E", changeTime: "12:15 ~ 13:35" },
    ],
  },
  ITM018: {
    production: { day: 1402, hour: 87 },
    work: { workTime: "15:00", idleTime: "01:45" },
    error: [
      { errorNumber: "E782", errorName: "온도 초과", errorTime: "16:30" },
      { errorNumber: "E794", errorName: "모터 정지", errorTime: "09:30" },
    ],
    mold: [{ moldName: "금형F", changeTime: "11:30 ~ 12:50" }],
  },
  ITM019: {
    production: { day: 1303, hour: 84 },
    work: { workTime: "09:30", idleTime: "01:45" },
    error: [{ errorNumber: "E999", errorName: "모터 정지", errorTime: "10:45" }],
    mold: [
      { moldName: "금형E", changeTime: "06:45 ~ 08:15" },
      { moldName: "금형F", changeTime: "09:00 ~ 10:30" },
    ],
  },
  ITM020: {
    production: { day: 1297, hour: 93 },
    work: { workTime: "10:15", idleTime: "00:15" },
    error: [
      { errorNumber: "E936", errorName: "온도 초과", errorTime: "13:30" },
      { errorNumber: "E468", errorName: "과부하", errorTime: "06:15" },
    ],
    mold: [{ moldName: "금형A", changeTime: "14:15 ~ 14:45" }],
  },
};
